import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import ScrollToTop from '@/components/base/ScrollToTop';
import SEOMeta from '@/components/base/SEOMeta';
import NewsCard from '@/components/feature/NewsCard';
import { Link } from 'react-router-dom';
import { articles, categories, featuredArticles, trendingArticles } from '@/mocks/articles';

const websiteUrl = 'https://leyusports.com';

const homeSchema = {
  '@context': 'https://schema.org',
  '@type': 'WebSite',
  name: '乐鱼体育资讯中心',
  url: websiteUrl,
  description: '乐鱼体育资讯中心汇聚全球最新体育新闻、赛事直播资讯、运动员动态及赛程安排，为体育爱好者提供权威、及时、全面的体育内容平台。',
  potentialAction: {
    '@type': 'SearchAction',
    target: `${websiteUrl}/search?q={search_term_string}`,
    'query-input': 'required name=search_term_string',
  },
};

const orgSchema = {
  '@context': 'https://schema.org',
  '@type': 'Organization',
  name: '乐鱼体育资讯中心',
  url: websiteUrl,
  description: '专注于体育资讯报道的综合媒体平台',
  contactPoint: {
    '@type': 'ContactPoint',
    telephone: '400-888-6688',
    contactType: 'customer service',
  },
};

export default function HomePage() {
  return (
    <>
      <SEOMeta
        title="乐鱼体育资讯中心 - 最新体育新闻与赛事动态"
        description="乐鱼体育资讯中心汇聚全球最新体育新闻、赛事直播资讯、运动员动态及赛程安排，为体育爱好者提供权威、及时、全面的体育内容平台，让您第一时间掌握足球、篮球、电竞等热门赛事资讯。"
        keywords="乐鱼体育资讯中心,乐鱼体育最新资讯,乐鱼体育赛事动态,乐鱼体育新闻,体育赛事资讯平台,乐鱼体育直播资讯,乐鱼赛程安排"
        canonicalUrl={websiteUrl}
        ogImage="https://readdy.ai/api/search-image?query=modern%20sports%20news%20media%20website%20header%20with%20dynamic%20sports%20elements%2C%20clean%20professional%20design%2C%20red%20and%20gold%20accents%2C%20Chinese%20sports%20media%20branding&width=1200&height=630&seq=home-og&orientation=landscape"
        schemaJson={{ '@context': 'https://schema.org', '@graph': [homeSchema, orgSchema] }}
      />
      <ScrollToTop />
      <Navbar />

      <main>
        <section className="relative w-full h-[600px] md:h-[700px] overflow-hidden">
          <img
            src="https://readdy.ai/api/search-image?query=epic%20sports%20stadium%20panorama%20with%20dramatic%20lighting%2C%20professional%20sports%20photography%2C%20dynamic%20athletic%20scenes%20collage%2C%20soccer%20basketball%20tennis%20esports%20blended%2C%20energetic%20and%20vibrant%20warm%20tones%2C%20editorial%20style%2C%20sports%20media&width=1600&height=900&seq=hero-bg&orientation=landscape"
            alt="乐鱼体育资讯中心"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-foreground-950/60 via-foreground-950/35 to-foreground-950/70"></div>
          <div className="absolute inset-0 flex flex-col items-center justify-center text-center px-4 w-full">
            <span className="inline-block px-4 py-1.5 bg-accent-500/90 text-background-50 text-sm font-medium rounded-full mb-6">
              全球体育资讯，一手掌握
            </span>
            <h1 className="font-heading text-3xl md:text-5xl lg:text-6xl font-black text-background-50 mb-6 max-w-4xl leading-tight">
              乐鱼体育资讯中心
            </h1>
            <p className="text-base md:text-lg text-background-200 max-w-2xl mb-8 leading-relaxed">
              汇聚全球最新体育新闻、赛事直播资讯、运动员动态及赛程安排，为您提供权威、及时、全面的体育内容平台
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <Link
                to="/category/football"
                className="px-8 py-3 bg-primary-500 text-background-50 rounded-full text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap cursor-pointer"
              >
                浏览最新资讯
              </Link>
              <Link
                to="/about"
                className="px-8 py-3 bg-background-50/15 text-background-50 border border-background-50/30 rounded-full text-sm font-semibold hover:bg-background-50/25 transition-colors whitespace-nowrap cursor-pointer"
              >
                了解更多
              </Link>
            </div>
          </div>
          <div className="absolute bottom-8 left-1/2 -translate-x-1/2 animate-bounce">
            <i className="ri-arrow-down-line text-background-50/60 text-2xl"></i>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-16">
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-4">
            {categories.filter(c => c.slug !== 'all').map((cat) => (
              <Link
                key={cat.slug}
                to={`/category/${cat.slug}`}
                className="flex flex-col items-center gap-3 p-5 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
              >
                <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl">
                  <i className={`ri-${cat.slug === 'football' ? 'football' : cat.slug === 'basketball' ? 'basketball' : cat.slug === 'esports' ? 'gamepad' : cat.slug === 'tennis' ? 'tennis' : 'trophy'}-line text-primary-600 text-xl`}></i>
                </div>
                <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">{cat.name}</span>
              </Link>
            ))}
            <Link
              to="/category/all"
              className="flex flex-col items-center gap-3 p-5 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200 hover:bg-primary-50/30 transition-all duration-300 cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center bg-primary-100 rounded-xl">
                <i className="ri-grid-line text-primary-600 text-xl"></i>
              </div>
              <span className="text-sm font-medium text-foreground-800 whitespace-nowrap">全部</span>
            </Link>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 pb-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">头条新闻</h2>
              <p className="text-sm text-foreground-500 mt-1">今日最重要的体育资讯</p>
            </div>
          </div>
          <div className="grid grid-cols-1 lg:grid-cols-5 gap-6">
            <div className="lg:col-span-3">
              {featuredArticles.slice(0, 1).map((article) => (
                <NewsCard key={article.id} {...article} size="large" />
              ))}
            </div>
            <div className="lg:col-span-2 flex flex-col gap-4">
              {featuredArticles.slice(1, 4).map((article) => (
                <NewsCard key={article.id} {...article} size="small" />
              ))}
            </div>
          </div>
        </section>

        <section className="bg-background-100 py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6">
            <div className="flex items-center justify-between mb-8">
              <div>
                <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">最新资讯</h2>
                <p className="text-sm text-foreground-500 mt-1">实时更新的体育新闻</p>
              </div>
              <Link to="/category/all" className="text-sm text-primary-500 hover:text-primary-600 font-medium flex items-center gap-1 whitespace-nowrap cursor-pointer">
                查看全部 <i className="ri-arrow-right-line"></i>
              </Link>
            </div>
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
              {articles.slice(4, 10).map((article) => (
                <NewsCard key={article.id} {...article} />
              ))}
            </div>
          </div>
        </section>

        <section className="max-w-[1280px] mx-auto px-4 md:px-6 py-16">
          <div className="flex items-center justify-between mb-8">
            <div>
              <h2 className="font-heading text-2xl md:text-3xl font-bold text-foreground-900">热门推荐</h2>
              <p className="text-sm text-foreground-500 mt-1">用户最关注的体育话题</p>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {trendingArticles.map((article) => (
              <Link
                key={article.id}
                to={`/article/${article.id}`}
                className="group flex gap-5 p-5 bg-background-50 rounded-xl border border-background-200/60 hover:border-primary-200 transition-all duration-300 cursor-pointer"
              >
                <div className="w-40 h-28 flex-shrink-0 rounded-lg overflow-hidden">
                  <img src={article.imageUrl} alt={article.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                </div>
                <div className="flex-1 min-w-0 flex flex-col justify-between">
                  <div>
                    <span className="text-xs text-accent-600 font-medium">{article.category}</span>
                    <h3 className="font-heading text-base font-semibold text-foreground-900 mt-1 line-clamp-2 group-hover:text-primary-500 transition-colors">
                      {article.title}
                    </h3>
                  </div>
                  <div className="flex items-center gap-3 text-xs text-foreground-400 mt-2">
                    <span>{article.author}</span>
                    <span>{article.publishDate}</span>
                    <span>{article.readTime}阅读</span>
                  </div>
                </div>
              </Link>
            ))}
          </div>
        </section>

        <section className="bg-accent-500 py-16">
          <div className="max-w-[1280px] mx-auto px-4 md:px-6 text-center">
            <h2 className="font-heading text-2xl md:text-3xl font-bold text-background-50 mb-4">不错过任何精彩瞬间</h2>
            <p className="text-background-100 mb-8 max-w-xl mx-auto">订阅乐鱼体育资讯中心，每日精选体育新闻直送您的邮箱</p>
            <form className="flex flex-col sm:flex-row gap-3 max-w-md mx-auto" data-readdy-form="true" action="https://readdy.ai/api/form/d9rgts7nahb7382ovmbg" method="POST">
              <input
                type="email"
                name="email"
                placeholder="请输入您的邮箱地址"
                className="flex-1 px-5 py-3 rounded-full text-sm bg-background-50 text-foreground-800 placeholder:text-foreground-400 focus:outline-none focus:ring-2 focus:ring-background-50/50"
                required
              />
              <input type="text" name="company_alt" tabIndex={-1} autoComplete="off" aria-hidden="true" readOnly className="honeypot-field" />
              <button type="submit" className="px-6 py-3 bg-foreground-950 text-background-50 rounded-full text-sm font-semibold hover:bg-foreground-900 transition-colors whitespace-nowrap cursor-pointer">
                立即订阅
              </button>
            </form>
          </div>
        </section>
      </main>

      <Footer />
    </>
  );
}